import { CartItemExtra, Modifier, ModifierGroup, SelectedModifier } from '../types';

/**
 * A group's full selectable universe — every modifier a guest can select,
 * `is_by_request` modifiers included. By-request modifiers carry no fixed
 * chargeable price, but they are still selectable, and a selection of one
 * counts toward its group's `min_selections`/`max_selections` bounds like
 * any other modifier — a required group whose only modifiers are all
 * by-request is satisfied once one of them is selected, matching the
 * backend's counting rule for by-request items.
 *
 * Exported so `ModifierGroupList` consumes this exact set rather than
 * keeping its own copy — the set the renderer offers and the set these
 * required-group helpers count can never diverge. A caller that must
 * exclude by-request modifiers (e.g. `defaultSelectionForGroups`) applies
 * its own `!modifier.is_by_request` filter on top of this full universe.
 */
export const selectableModifiersForGroup = (group: ModifierGroup): Modifier[] => group.modifiers;

/**
 * Distinct count of selected modifiers (deduped by `modifier_id`) that
 * belong to this group's full selectable universe, `is_by_request`
 * modifiers included. Counts distinct selections, never summed quantity —
 * a single modifier selected at quantity 5 still counts as one selection
 * against a group's `min_selections`/`max_selections` bounds.
 */
const distinctSelectionCountForGroup = (
  group: ModifierGroup,
  selection: SelectedModifier[],
): number => {
  const selectableModifierIds = new Set(
    selectableModifiersForGroup(group).map((modifier) => modifier.id),
  );
  const selectedDistinctIds = new Set(
    selection
      .filter((selectedModifier) => selectableModifierIds.has(selectedModifier.modifier_id))
      .map((selectedModifier) => selectedModifier.modifier_id),
  );
  return selectedDistinctIds.size;
};

/**
 * Initial selection derived from each modifier's `is_default_selected`
 * flag, at quantity 1. Applies its own `!modifier.is_by_request` filter on
 * top of the full selectable universe — `is_by_request` modifiers carry no
 * fixed chargeable price and must never enter selection state
 * automatically, even when flagged default-selected.
 */
export const defaultSelectionForGroups = (groups: ModifierGroup[]): SelectedModifier[] =>
  groups.flatMap((group) =>
    selectableModifiersForGroup(group)
      .filter((modifier) => modifier.is_default_selected && !modifier.is_by_request)
      .map((modifier) => ({ modifier_id: modifier.id, quantity: 1 })),
  );

/**
 * Every group whose `min_selections` bound is not met by the current
 * selection's distinct count within that group's full selectable universe
 * (`is_by_request` modifiers included — the counting rule matches the
 * backend).
 */
export const unsatisfiedRequiredGroups = (
  groups: ModifierGroup[],
  selection: SelectedModifier[],
): ModifierGroup[] =>
  groups.filter(
    (group) =>
      group.min_selections >= 1 &&
      distinctSelectionCountForGroup(group, selection) < group.min_selections,
  );

/** True when every required group (`min_selections >= 1`) is satisfied. */
export const areRequiredGroupsSatisfied = (
  groups: ModifierGroup[],
  selection: SelectedModifier[],
): boolean => unsatisfiedRequiredGroups(groups, selection).length === 0;

/**
 * Resolves a guest's selection to a DISPLAY-ONLY snapshot (`CartItemExtra[]`)
 * — each entry's guest-facing label and by-request flag, looked up by
 * `modifier_id` across every group's `modifiers` (order-independent; a
 * modifier belongs to exactly one group). Preserves selection order. Drops
 * any entry whose `modifier_id` matches no modifier in any group (a stale
 * or malformed selection) and any entry whose `quantity` is not positive —
 * pure, no side effects. Callers persist the result alongside the cart item
 * (see `CartItem.selectedExtras`); it is never sent on the checkout wire.
 */
export const buildSelectedExtras = (
  groups: ModifierGroup[],
  selection: SelectedModifier[],
): CartItemExtra[] => {
  const modifiersById = new Map<number, Modifier>();
  groups.forEach((group) => {
    group.modifiers.forEach((modifier) => {
      modifiersById.set(modifier.id, modifier);
    });
  });

  return selection.reduce<CartItemExtra[]>((extras, selected) => {
    if (selected.quantity <= 0) return extras;
    const modifier = modifiersById.get(selected.modifier_id);
    if (!modifier) return extras;
    extras.push({
      modifier_id: modifier.id,
      label: modifier.name,
      quantity: selected.quantity,
      is_by_request: modifier.is_by_request,
    });
    return extras;
  }, []);
};

/**
 * True when the current selection includes at least one `is_by_request`
 * modifier, across any of the given groups. Hosts fork their
 * call-to-action on this — a selection containing a by-request modifier
 * cannot be priced or added to cart along the normal chargeable path.
 */
export const selectionContainsByRequest = (
  groups: ModifierGroup[],
  selection: SelectedModifier[],
): boolean => {
  const selectedModifierIds = new Set(selection.map((entry) => entry.modifier_id));
  return groups.some((group) =>
    group.modifiers.some(
      (modifier) => modifier.is_by_request && selectedModifierIds.has(modifier.id),
    ),
  );
};

/** True when any modifier in the groups is priced per guest. */
export const hasPerGuestModifier = (groups: ModifierGroup[]): boolean =>
  groups.some((group) => group.modifiers.some((modifier) => modifier.price_mode === 'PER_GUEST'));
